import { saveAs } from "file-saver";
import { fileNamefromUrl } from "utils";
import { toCheckLocationNumber } from "./event-check-location-helper";
import {
    canvasToPngBlob,
    createCheckQrPosterCanvas,
    createCheckQrPosterPdf,
} from "./event-check-qr-poster-helper";

export const getCheckQrFileName = (url, eventID, extension = "png") => {
    try
    {
        return fileNamefromUrl(url) || `exhibition-${ eventID }-check-qr.${ extension }`;
    } catch (error)
    {
        return `exhibition-${ eventID }-check-qr.${ extension }`;
    }
};

export const getCheckQrCoordinatesKey = (event) => [
    toCheckLocationNumber(event?.check_latitude),
    toCheckLocationNumber(event?.check_longitude),
    toCheckLocationNumber(event?.check_radius),
].join(":");

export const getCheckQrStorageKey = (eventID) => `event-check-qr-coordinates-${ eventID }`;

export const getCheckQrRevisionStorageKey = (eventID) => `event-check-qr-revision-${ eventID }`;

export const readCheckQrRevision = (eventID) => {
    if (!eventID) return null;

    try
    {
        return window.localStorage.getItem(getCheckQrRevisionStorageKey(eventID));
    } catch (error)
    {
        return null;
    }
};

export const writeCheckQrRevision = (eventID, revision) => {
    if (!eventID || !revision) return;

    try
    {
        window.localStorage.setItem(getCheckQrRevisionStorageKey(eventID), revision);
    } catch (error)
    {
        // A versioned server URL still refreshes normally when storage is unavailable.
    }
};

export const removeCheckQrRevision = (eventID) => {
    if (!eventID) return;

    try
    {
        window.localStorage.removeItem(getCheckQrRevisionStorageKey(eventID));
    } catch (error)
    {
        // Ignore best-effort cache metadata cleanup failures.
    }
};

export const withCheckQrRevision = (url, revision) => {
    if (!url || !revision) return url;

    const hashIndex = url.indexOf("#");
    const baseUrl = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
    const revisionParam = `check_qr_revision=${ encodeURIComponent(revision) }`;
    const nextBaseUrl = /([?&])check_qr_revision=[^&]*/.test(baseUrl) ?
        baseUrl.replace(/([?&])check_qr_revision=[^&]*/, `$1${ revisionParam }`) :
        `${ baseUrl }${ baseUrl.includes("?") ? "&" : "?" }${ revisionParam }`;

    return `${ nextBaseUrl }${ hash }`;
};

export const getCheckQrPosterFileName = (eventID, extension) =>
    `exhibition-${ eventID }-check-in-qr.${ extension }`;

export const downloadCheckQrPng = async (paramsOrFactory) => {
    const params = typeof paramsOrFactory === "function" ? await paramsOrFactory() : paramsOrFactory;

    if (!params?.url) return false;

    const canvas = await createCheckQrPosterCanvas(params);

    saveAs(await canvasToPngBlob(canvas), getCheckQrPosterFileName(params.eventID, "png"));

    return true;
};

export const downloadCheckQrPdf = async (paramsOrFactory) => {
    const params = typeof paramsOrFactory === "function" ? await paramsOrFactory() : paramsOrFactory;

    if (!params?.url) return false;

    const doc = await createCheckQrPosterPdf(params);
    const pdfBlob = doc.output("blob");

    saveAs(pdfBlob, getCheckQrPosterFileName(params.eventID, "pdf"));

    return true;
};

export const printCheckQr = async (paramsOrFactory) => {
    if (typeof paramsOrFactory !== "function" && !paramsOrFactory?.url) return false;

    const printWindow = window.open("", "_blank", "width=640,height=720");

    if (!printWindow) return false;

    printWindow.document.title = "QR Code";
    printWindow.document.body.textContent = "Preparing PDF...";

    try
    {
        const params = typeof paramsOrFactory === "function" ? await paramsOrFactory() : paramsOrFactory;

        if (!params?.url)
        {
            printWindow.close();
            return false;
        }

        const doc = await createCheckQrPosterPdf(
            params,
            { autoPrint: true }
        );
        const pdfUrl = URL.createObjectURL(doc.output("blob"));

        printWindow.location.replace(pdfUrl);
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);

        return true;
    } catch (error)
    {
        printWindow.close();
        throw error;
    }
};
